import { Classes, Intent, Placement } from "@blueprintjs/core";
import {
  OrgRole,
  OrgRoleRequestGatedAction,
  OrgRoleRequestSource,
  orgRoleHasEditPermissions,
} from "@hex/common";
import React, { memo, useMemo } from "react";
import styled from "styled-components";

import { HexButtonWithDropdownProps } from "../../hex-components/HexButtonWithDropdown.js";
import { HexButtonWithDropdown, HexMenu } from "../../hex-components/index.js";
import { useCurrentUser } from "../../hooks/me/useCurrentUser.js";
import { useOrgExplorerRoleEnabled } from "../../hooks/me/useOrgExplorerRoleEnabled.js";
import { HomeTab } from "../../route/routes.js";
import CollectionsGateIcon from "../../static/illustration/illustration-featuregate-collections.inline.svg";
import ComponentFeatureGateIcon from "../../static/illustration/illustration-featuregate-components.inline.svg";
import ProjectsGateIcon from "../../static/illustration/illustration-featuregate-projects.inline.svg";
import { HotKeys } from "../../util/hotkeys.js";
import { TestSelectors } from "../../util/testSelectors.js";
import { FeatureGateToolTip } from "../feature-gate/FeatureGateToolTip.js";
import { NewCollectionDialog } from "../home/collections-tab/dialogs/NewCollectionDialog";
import { AddIcon } from "../icons/CustomIcons.js";
import { ImportNewComponentDialog } from "../upload/ImportComponentDialog";
import { ImportNewProjectDialog } from "../upload/ImportProjectDialog";
import { NewComponentDialog } from "../upload/NewComponentDialog";
import { NewProjectDialog } from "../upload/NewProjectDialog";

import {
  CreateComponentMenuItem,
  CreateProjectMenuItem,
  CreateRComponentMenuItem,
  CreateRProjectMenuItem,
  ImportComponentMenuItem,
  ImportProjectMenuItem,
} from "./create-items/CreateMenuItems";
import {
  useCreateCollectionButton,
  useCreateComponentButton,
  useCreateExplorationButton,
  useCreateProjectButton,
  useCreateThreadButton,
} from "./create-items/useCreateAssets";
import { RoleRequestTooltip } from "./RoleRequestTooltip";
import { useHexFlag } from "../../util/useHexFlags.js";

interface CreateNewButtonsProps {
  selectedTab?: HomeTab;
  buttonPropsOverrides?: Partial<HexButtonWithDropdownProps>;
  placement?: Placement;
}

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: flex-end;
  text-wrap: nowrap;
  > .${Classes.POPOVER_TARGET} {
    width: 100%;
  }
`;

export const CreateAssetDialogs = memo(function CreateAssetDialogs() {
  const currentUser = useCurrentUser();
  const orgRole = currentUser?.orgRole;
  const uploadOrCreateDisabled =
    !orgRole ||
    orgRole === OrgRole.GUEST ||
    !orgRoleHasEditPermissions(orgRole);

  return (
    <>
      {!uploadOrCreateDisabled && (
        <>
          <NewProjectDialog />
          <NewComponentDialog />
          <ImportNewProjectDialog />
          <ImportNewComponentDialog />
        </>
      )}
      <NewCollectionDialog />
    </>
  );
});

export const CreateNewButtons = memo(function CreateNewButtons({
  buttonPropsOverrides,
  placement = "bottom-start",
  selectedTab,
}: CreateNewButtonsProps) {
  const exploreRoleEnabled = useOrgExplorerRoleEnabled();
  const allowRProjectCreation = useHexFlag("allow-r-project-creation");

  const {
    createNewProject,
    disabledForRole: createProjectDisabledForRole,
    newProjectDisabled,
    projectLimit,
    projectLimitEnforced,
  } = useCreateProjectButton({
    location: null,
  });
  const {
    createPythonComponent,
    disabledForRole: createComponentDisabledForRole,
    hasFeatureGate: componentsFeatureGate,
    newComponentDisabled,
  } = useCreateComponentButton({
    location: null,
  });
  const {
    disabledForRole: createCollectionDisabledForRole,
    hasFeatureGate: collectionsFeatureGate,
    newCollectionDisabled,
    openNewCollectionDialog,
  } = useCreateCollectionButton({
    location: null,
  });
  const {
    canCreateNewThread,
    createNewThread,
    disabledForRole: createThreadDisabledForRole,
    hasFeatureGate: createThreadFeatureGate,
    minimumRoleForAccess: createThreadMinimumRoleForAccess,
  } = useCreateThreadButton({
    location: null,
  });
  const {
    createNewExplore,
    disabledForRole: createExplorationDisabledForRole,
    hasFeatureGate: exploreFromScratchFeatureGate,
    newExplorationDisabled,
  } = useCreateExplorationButton({
    location: null,
  });

  const popoverContents = useMemo(() => {
    if (selectedTab === "all") {
      return (
        <HexMenu>
          <CreateProjectMenuItem location={null} placement={placement} />
          {allowRProjectCreation && (
            <CreateRProjectMenuItem location={null} placement={placement} />
          )}
          <ImportProjectMenuItem location={null} placement={placement} />
        </HexMenu>
      );
    } else if (selectedTab === "components") {
      return (
        <HexMenu>
          <CreateComponentMenuItem location={null} placement={placement} />
          {allowRProjectCreation && (
            <CreateRComponentMenuItem location={null} placement={placement} />
          )}
          <ImportComponentMenuItem location={null} placement={placement} />
        </HexMenu>
      );
    } else {
      // empty menu, we shouldn't be rendering the dropdown in this case
      return undefined;
    }
  }, [selectedTab, placement, allowRProjectCreation]);

  const defaultButtonProps: HexButtonWithDropdownProps = useMemo(() => {
    return {
      hideDropdown: !popoverContents,
      buttonContent: "",
      icon: <AddIcon />,
      intent: Intent.SUCCESS,
      popoverContent: popoverContents,
      small: false,
      subtle: false,
      ...buttonPropsOverrides,
    };
  }, [buttonPropsOverrides, popoverContents]);

  const newProjectButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Unlock unlimited projects with a paid plan."
        disabled={!projectLimitEnforced}
        featureGate="projectLimit"
        icon={<ProjectsGateIcon />}
        placement={placement}
        titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New project"
          disabled={newProjectDisabled}
          dropdownButtonDataTest={TestSelectors.NEW_PROJECT_DROPDOWN}
          primaryButtonDataTest={TestSelectors.CREATE_PROJECT}
          tooltip={{
            combo: HotKeys.NEW_PROJECT,
            disabled: newProjectDisabled,
            hotkeyName: "Create a new project",
          }}
          onClick={createNewProject}
        />
      </FeatureGateToolTip>
    ),
    [
      createNewProject,
      defaultButtonProps,
      newProjectDisabled,
      projectLimit,
      projectLimitEnforced,
      placement,
    ],
  );

  const newComponentButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsFeatureGate}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New component"
          disabled={newComponentDisabled}
          dropdownButtonDataTest={TestSelectors.NEW_COMPONENT_DROPDOWN}
          primaryButtonDataTest={TestSelectors.CREATE_COMPONENT}
          tooltip={{
            combo: HotKeys.NEW_COMPONENT,
            disabled: newComponentDisabled,
            hotkeyName: "Create a new component",
          }}
          onClick={createPythonComponent}
        />
      </FeatureGateToolTip>
    ),
    [
      componentsFeatureGate,
      createPythonComponent,
      defaultButtonProps,
      newComponentDisabled,
      placement,
    ],
  );

  const newCollectionButton = useMemo(
    () => (
      <FeatureGateToolTip
        descriptionOverride="Collections give you the ability to organize and share your projects."
        disabled={collectionsFeatureGate}
        featureGate="collections"
        icon={<CollectionsGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable collections"
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New collection"
          disabled={newCollectionDisabled}
          tooltip={{
            combo: HotKeys.NEW_COLLECTION,
            disabled: newCollectionDisabled,
            hotkeyName: "Create a new collection",
          }}
          // eslint-disable-next-line react/jsx-no-bind -- this button rerendering should be harmless
          onClick={() => {
            openNewCollectionDialog();
          }}
        />
      </FeatureGateToolTip>
    ),
    [
      collectionsFeatureGate,
      defaultButtonProps,
      newCollectionDisabled,
      openNewCollectionDialog,
      placement,
    ],
  );

  const newExplorationButton = useMemo(
    () => (
      <FeatureGateToolTip
        action="explore outside of a published app"
        disabled={exploreFromScratchFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New exploration"
          disabled={newExplorationDisabled}
          onClick={createNewExplore}
        />
      </FeatureGateToolTip>
    ),
    [
      createNewExplore,
      defaultButtonProps,
      exploreFromScratchFeatureGate,
      newExplorationDisabled,
      placement,
    ],
  );

  const newThreadButton = useMemo(() => {
    return (
      <FeatureGateToolTip
        action="create a new thread"
        disabled={createThreadFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <HexButtonWithDropdown
          {...defaultButtonProps}
          buttonContent="New thread"
          disabled={!canCreateNewThread}
          dropdownButtonDataTest={TestSelectors.NEW_THREAD_DROPDOWN}
          primaryButtonDataTest={TestSelectors.CREATE_THREAD}
          onClick={createNewThread}
        />
      </FeatureGateToolTip>
    );
  }, [
    canCreateNewThread,
    createNewThread,
    defaultButtonProps,
    createThreadFeatureGate,
    placement,
  ]);

  const {
    createNewCTA,
    disabledForRole,
    gatedAction,
    hasFeatureGate,
    requestSource,
    role,
    typeOfEntity,
  } = useMemo(() => {
    switch (selectedTab) {
      case "components":
        return {
          typeOfEntity: "components",
          hasFeatureGate: componentsFeatureGate,
          disabledForRole: createComponentDisabledForRole,
          createNewCTA: newComponentButton,
          gatedAction: OrgRoleRequestGatedAction.CREATE_COMPONENT,
          requestSource: OrgRoleRequestSource.CREATE_COMPONENT,
          role: OrgRole.EDITOR,
        };
      case "explorations":
        return {
          typeOfEntity: "explorations",
          hasFeatureGate: exploreFromScratchFeatureGate,
          disabledForRole: createExplorationDisabledForRole,
          createNewCTA: newExplorationButton,
          gatedAction: OrgRoleRequestGatedAction.EXPLORE,
          requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
          role: exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR,
        };
      case "collections":
        return {
          typeOfEntity: "collections",
          hasFeatureGate: collectionsFeatureGate,
          createNewCTA: newCollectionButton,
          disabledForRole: createCollectionDisabledForRole,
          gatedAction: OrgRoleRequestGatedAction.CREATE_COLLECTION,
          requestSource: OrgRoleRequestSource.CREATE_COLLECTION,
          role: OrgRole.EDITOR,
        };
      case "threads":
        return {
          typeOfEntity: "threads",
          createNewCTA: newThreadButton,
          hasFeatureGate: createThreadFeatureGate,
          disabledForRole: createThreadDisabledForRole,
          gatedAction: OrgRoleRequestGatedAction.CREATE_THREAD,
          requestSource: OrgRoleRequestSource.CREATE_PROJECT,
          role: createThreadMinimumRoleForAccess,
        };
      case "home":
        if (createProjectDisabledForRole) {
          if (canCreateNewThread) {
            return {
              typeOfEntity: "threads",
              createNewCTA: newThreadButton,
              hasFeatureGate: createThreadFeatureGate,
              disabledForRole: false,
              gatedAction: OrgRoleRequestGatedAction.CREATE_THREAD,
              requestSource: OrgRoleRequestSource.CREATE_PROJECT,
              role: createThreadMinimumRoleForAccess,
            };
          } else if (!newExplorationDisabled) {
            return {
              typeOfEntity: "explorations",
              hasFeatureGate: exploreFromScratchFeatureGate,
              disabledForRole: false,
              createNewCTA: newExplorationButton,
              gatedAction: OrgRoleRequestGatedAction.EXPLORE,
              requestSource: OrgRoleRequestSource.CREATE_EXPLORE,
              role: exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR,
            };
          }
        }
      // Fall through
      default:
        return {
          typeOfEntity: "projects",
          hasFeatureGate: !projectLimitEnforced,
          disabledForRole: createProjectDisabledForRole,
          createNewCTA: newProjectButton,
          gatedAction: OrgRoleRequestGatedAction.CREATE_PROJECT,
          requestSource: OrgRoleRequestSource.CREATE_PROJECT,
          role: OrgRole.EDITOR,
        };
    }
  }, [
    selectedTab,
    createComponentDisabledForRole,
    newComponentButton,
    createExplorationDisabledForRole,
    newExplorationButton,
    exploreRoleEnabled,
    newCollectionButton,
    createCollectionDisabledForRole,
    newThreadButton,
    createThreadDisabledForRole,
    createThreadMinimumRoleForAccess,
    createProjectDisabledForRole,
    newProjectButton,
    canCreateNewThread,
    newExplorationDisabled,
    collectionsFeatureGate,
    componentsFeatureGate,
    createThreadFeatureGate,
    exploreFromScratchFeatureGate,
    projectLimitEnforced,
  ]);

  if (disabledForRole) {
    return (
      <ButtonWrapper>
        <RoleRequestTooltip
          contentText={`You do not have permission to create ${typeOfEntity}.`}
          disabled={!hasFeatureGate}
          gatedAction={gatedAction}
          placement={placement}
          requestSource={requestSource}
          role={role}
        >
          {createNewCTA}
        </RoleRequestTooltip>
      </ButtonWrapper>
    );
  }

  return <ButtonWrapper>{createNewCTA}</ButtonWrapper>;
});
